import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, X, Settings2 } from 'lucide-react';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const AudioPlayer = ({ audio, setAudio }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [audioUrl, setAudioUrl] = useState(audio.url);
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  useEffect(() => {
    const fetchAndPlayAudio = async () => {
      if (!isPlaying) {
        audioRef.current?.pause();
        return;
      }

      try {
        setLoading(true);
        
        // Fetch the audio URL to handle potential 403 errors
        const response = await fetch(audio.url);
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message)
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setAudioUrl(blobUrl);
        
        await audioRef.current?.play();
        setLoading(false);
      } catch (error) {
        setAudio({  
          ...audio,
          error: error.message
        });
        setLoading(false);
      }
    };

    fetchAndPlayAudio();

    // Cleanup blob URL on unmount
    return () => {
      if (audioUrl !== audio.url) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [isPlaying, audio.url]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleProgressClick = (e) => {
    if (progressRef.current && audioRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      const time = (percentage / 100) * audioRef.current.duration;
      audioRef.current.currentTime = time;
      setProgress(percentage);
    }
  };

  const handleLoadStart = () => setLoading(true);
  const handleCanPlay = () => setLoading(false);
  const handleError = (error) => {
    setAudio({
      ...audio,
      error: error.message
    });
    setLoading(false);
  };

  const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];

  return (
    <div className="fixed bottom-0 left-0 right-0 flex justify-center border-t bg-white py-2">
      <div className="w-full max-w-3xl bg-white p-4">
        <div className="flex items-center gap-4">
          <>
            <button
              onClick={() => setIsPlaying(!isPlaying)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              >
                {loading ? (
                  <Settings2 className="w-4 h-4 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </button>

              <div className="flex-1">
                <div className={`text-sm font-medium mb-2 ${audio.error ? 'text-red-500' : ''}`}>{audio.error || audio.title}</div>
                <div
                  ref={progressRef}
                  onClick={handleProgressClick}
                  className="h-2 bg-gray-200 rounded-full cursor-pointer relative overflow-hidden"
                >
                  <div
                    className="absolute top-0 left-0 h-full bg-gray-300 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                  <div
                    className="absolute top-0 left-0 h-full bg-blue-500 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      onClick={() => {
                        const currentIndex = speeds.indexOf(playbackRate);
                        const nextIndex = (currentIndex + 1) % speeds.length;
                        setPlaybackRate(speeds[nextIndex]);
                      }}
                      className="px-2 py-1 text-sm rounded hover:bg-gray-100"
                    >
                      {playbackRate}x
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Playback Speed</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <button
                onClick={() => setAudio({})}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
          </>
        </div>

        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadStart={handleLoadStart}
          onCanPlay={handleCanPlay}
          onError={handleError}
          autoPlay
        />
      </div>
    </div>
  );
};

export default AudioPlayer;